import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { parseAuthoriztionToken } from 'src/app/util';
import * as i0 from "@angular/core";
import * as i1 from "./common-http-client.service";
import * as i2 from "@angular/router";
import * as i3 from "@techmore/global";
/** 管理授權權杖服務 */
export class TokenService {
    constructor(http, router, globalComponentService, platformId) {
        this.http = http;
        this.router = router;
        this.globalComponentService = globalComponentService;
        this.platformId = platformId;
        /** 是否正在刷新token中 */
        this._isRefreshingToken = false;
        this._tokenRefreshed$ = new Subject();
        /** 登出剩餘時間 */
        this._logoutTimer$ = new BehaviorSubject(null);
        /** 是否正在登出中 */
        this._isLoggingOut = false;
        this.auth_token_key = 'auth_token';
        this.auth_refresh_token_key = 'auth_refresh_token';
        this.jwt_payload_key = 'memberInfo';
        this.auth_way_key = 'auth_way';
        /** 登出行為，可透過 registCustomLogout 方法覆寫 */
        this._customLogout = () => {
            return this.router.navigate(['/login']);
        };
    }
    /** 是否正在刷新token中 */
    get isRefreshingToken() { return this._isRefreshingToken; }
    /** 是否正在登出中 */
    get isLoggingOut() { return this._isLoggingOut; }
    get userInfo() {
        const jwtPayload = this.getJWTpayload();
        return Object.assign({ userName: jwtPayload && jwtPayload.sub }, jwtPayload);
    }
    /** 進入應授權落腳頁就馬上調用
     * @throws 若授權不存在就拋出Error
     */
    init() {
        if (isPlatformBrowser(this.platformId)) {
            this.setLogoutTimer();
        }
    }
    /** 取得登出剩餘時間，每次更新權杖/進入頁面初始化後才會推播一次剩餘秒數 */
    getLogoutTimer$() {
        return this._logoutTimer$.asObservable();
    }
    /** 更新存取根
     * @throws 若授權不存在就拋出Error
     */
    refreshToken$(apiObs) {
        if (this._isRefreshingToken) {
            return new Observable(observer => {
                const subc = this._tokenRefreshed$.subscribe(() => {
                    subc.unsubscribe();
                    observer.next();
                    observer.complete();
                });
            });
        }
        this._isRefreshingToken = true;
        return (apiObs ? apiObs : this.http.post(`${environment.apiHost}/system/oauth/token/refresh`, {}, { headers: { Authorization: 'Bearer ' + localStorage.getItem(this.auth_refresh_token_key) } })).pipe(tap(((jwtToken) => {
            this._isRefreshingToken = false;
            const refreshToken = jwtToken['refreshToken'];
            const accessToken = jwtToken['accessToken'];
            localStorage.setItem(this.auth_token_key, accessToken);
            localStorage.setItem(this.auth_refresh_token_key, refreshToken);
            // localStorage.setItem(this.jwt_payload_key, parseAuthoriztionToken(accessToken));
            this.setLogoutTimer();
            this._tokenRefreshed$.next();
        })), catchError((err) => {
            console.error('refreshing Token error');
            // this.globalComponentService.openErrorAlert('頁面閒置太久，請重新登入');
            this._isRefreshingToken = false;
            this._tokenRefreshed$.error(err);
            if (this.canLogout()) {
                this.logout();
            }
            return throwError(() => err);
        }));
    }
    /** 是否可以登出 */
    canLogout() {
        return !this._isLoggingOut && !!localStorage.getItem(this.auth_token_key);
    }
    /** 系統登出
     * @returns 返回承諾導航結果，若操作前已登出或正在登出中則 resolve false
     */
    logout() {
        return new Promise((resolve, reject) => {
            if (!this.canLogout()) {
                resolve(false);
                return;
            }
            this._isLoggingOut = true;
            this._customLogout().then(_ => {
                localStorage.removeItem(this.auth_refresh_token_key);
                localStorage.removeItem(this.auth_token_key);
                localStorage.removeItem(this.jwt_payload_key);
                localStorage.removeItem(this.auth_way_key);
                this._logoutTimer$.next(0);
                this._isLoggingOut = false;
                resolve(_);
            }).catch(error => {
                localStorage.removeItem(this.auth_refresh_token_key);
                localStorage.removeItem(this.auth_token_key);
                localStorage.removeItem(this.jwt_payload_key);
                localStorage.removeItem(this.auth_way_key);
                this._logoutTimer$.next(0);
                this._isLoggingOut = false;
                reject(error);
            });
        });
    }
    /** 設定距離登出時間剩餘多少秒數
     * @throws 若授權不存在就拋出Error
     */
    setLogoutTimer() {
        const expDate = this.getExpTime();
        const expirationDuration = Math.floor((expDate.getTime() - Date.now()) / 1000); // 從 token 獲取剩餘秒數
        this._logoutTimer$.next(expirationDuration < 0 ? 0 : expirationDuration);
    }
    /** 取得授權權杖
     * @throws 若授權不存在就拋出Error
     */
    getAuthToken() {
        const authorization = localStorage.getItem(this.auth_token_key);
        if (authorization) {
            return authorization;
        }
        else {
            throw new Error('Token not existing');
        }
    }
    /** 取得更新授權權杖
     * @throws 若授權不存在就拋出Error
     */
    getRefreshToken() {
        const authorization = localStorage.getItem(this.auth_refresh_token_key);
        if (authorization) {
            return authorization;
        }
        else {
            throw new Error('Token not existing');
        }
    }
    /** 取得權杖解析後的資料包
     * @throws 若授權不存在就拋出Error
     */
    getJWTpayload() {
        const authorization = localStorage.getItem(this.jwt_payload_key);
        if (authorization) {
            return JSON.parse(authorization);
        }
        else {
            return JSON.parse(parseAuthoriztionToken(this.getAuthToken()));
        }
    }
    /** 取得TOKEN過期時間
     * @throws 若授權不存在就拋出Error
     */
    getExpTime() {
        const p = this.getJWTpayload();
        return new Date(p.exp * 1000);
    }
    /** 檢查 更新存取根 到期時間是否未到要求的時間點
     * @returns 超過指定時間回傳真 ，否則為假
     * @throws 若授權不存在就拋出Error
     */
    checkRefreshTokenExpiredTime(endDate) {
        const refreshToken = this.getRefreshToken();
        const refreshInfo = parseAuthoriztionToken(refreshToken);
        const pareInfo = JSON.parse(refreshInfo);
        const refreshExp = pareInfo.exp;
        if (refreshExp < endDate.getTime() / 1000) {
            return true;
        }
        return false;
    }
    /** 取得登入後要自動轉導回的頁面 */
    getRedirectUrl() {
        return this._redirectUrl;
    }
    /** 設定登入後要自動轉導回的頁面 */
    setRedirectUrl(url) {
        this._redirectUrl = url;
    }
    /** 清除登入後要自動轉導回的頁面 */
    clearRedirectUrl() {
        delete this._redirectUrl;
    }
    /** 註冊自訂登出行為 */
    registCustomLogout(func) {
        if (func) {
            this._customLogout = func;
        }
    }
}
TokenService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function TokenService_Factory() { return new TokenService(i0.ɵɵinject(i1.CommonHttpClientService), i0.ɵɵinject(i2.Router), i0.ɵɵinject(i3.GlobalComponentService), i0.ɵɵinject(i0.PLATFORM_ID)); }, token: TokenService, providedIn: "root" });
